import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, EnhancedAutocomplete, FullRow, IconButton, Tooltip, Typography, Row, FullColumn } from 'seagull';
import { CloseCross, Copy, Edit, Eye, Import, Info, Trash } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { forbiddenTooltipText } from '~/infra/access-control';
import { legalTermsModel, UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { useLegalTerms } from '../../hooks/reactQuery/queries/useLegalTerms';
import { useCreateQuillLegalTerms } from './hooks/useCreateQuillLegalTerms';
import { useDeleteLegalTerms } from './hooks/useDeleteLegalTerms';
import { useDuplicateLegalTerms } from './hooks/useDuplicateLegalTerms';
import { useEditQuillLegalTerms } from './hooks/useEditQuillLegalTerms';
import { useRenameLegalTerms } from './hooks/useRenameLegalTerms';
import { useUploadLegalTermsWithCustomName } from './hooks/useUploadLegalTermsWithCustomName/useUploadLegalTermsWithCustomName';
import { useViewLegalTerms } from './hooks/useViewLegalTerms';
import styles from './LegalTermsDropdown.module.scss';
export const GetLegalTermsDropdownTID = (testId)=>getTestIds(testId);
export const LegalTermsDropdown = (param)=>{
    let { selectedLegalTermId, onSelectLegalTerm, testId, disabled, entityLegalTerms, onRemoveDropdown, onDeleteLegalTerm, viewMode: initialViewMode = 'basic', error, disabledItems, inputRef, editContext, sourceId, onLocalSave, resetLocalContentKey } = param;
    const { UPDATE_DEFAULT_TOS_FE } = useFeatureFlags();
    const { t: legalTermsTexts } = useLocaleKeys('components.legalTermsDropdown');
    const role = useStore(selectors.user.role);
    const isMember = role === UserRole.Member;
    const viewMode = isMember ? 'member' : initialViewMode;
    const isAdvancedViewMode = viewMode === 'advanced';
    const isBasicViewMode = viewMode === 'basic';
    const isMemberViewMode = viewMode === 'member';
    const { LegalTermsDropdownTID } = useMemo(()=>GetLegalTermsDropdownTID(testId), [
        testId
    ]);
    const history = useHistory();
    const { legalTerms, isLoading: isLoadingLegalTerms } = useLegalTerms();
    const { viewLegalTerms } = useViewLegalTerms();
    const { deleteLegalTerms, isPending: isDeletingLegalTerms } = useDeleteLegalTerms({
        onSuccess: async (legalTermsId)=>{
            await (onDeleteLegalTerm === null || onDeleteLegalTerm === void 0 ? void 0 : onDeleteLegalTerm(legalTermsId));
        },
        confirmActionTestId: LegalTermsDropdownTID.DeleteLegalTermConfirmModal()
    });
    const { renameLegalTerms, isPending: isRenamingLegalTerms } = useRenameLegalTerms({
        errorToastTestId: LegalTermsDropdownTID.RenameErrorToast()
    });
    const { createQuillLegalTerms } = useCreateQuillLegalTerms();
    const { duplicateLegalTerms } = useDuplicateLegalTerms();
    const { editQuillLegalTerms } = useEditQuillLegalTerms({
        onLocalSave,
        resetKey: resetLocalContentKey
    });
    const fileInputRef = useRef(null);
    const [replacedLegacyIds, setReplacedLegacyIds] = useState(new Set());
    const allLegalTerms = useMemo(()=>{
        let merged = legalTerms;
        if (entityLegalTerms) {
            const entityById = new Map(entityLegalTerms.map((lt)=>[
                    lt.id,
                    lt
                ]));
            merged = legalTerms.map((lt)=>{
                const entity = entityById.get(lt.id);
                if ((entity === null || entity === void 0 ? void 0 : entity.richTextId) && lt.contentType !== 'pdf') {
                    return _object_spread_props(_object_spread({}, lt), {
                        name: entity.name,
                        richTextId: entity.richTextId,
                        richTextContent: []
                    });
                }
                return lt;
            });
            const existingIds = new Set(legalTerms.map((lt)=>lt.id));
            const uniqueEntityLegalTerms = entityLegalTerms.filter((entityLT)=>(entityLT === null || entityLT === void 0 ? void 0 : entityLT.id) && !existingIds.has(entityLT === null || entityLT === void 0 ? void 0 : entityLT.id));
            merged = [
                ...merged,
                ...uniqueEntityLegalTerms
            ];
        }
        if (UPDATE_DEFAULT_TOS_FE && replacedLegacyIds.size > 0) {
            merged = merged.filter((lt)=>!replacedLegacyIds.has(lt.id));
        }
        return merged;
    }, [
        entityLegalTerms,
        legalTerms,
        UPDATE_DEFAULT_TOS_FE,
        replacedLegacyIds
    ]);
    const { uploadLegalTerms, isLoading: isUploadingLegalTerms } = useUploadLegalTermsWithCustomName({
        errorToastTestId: LegalTermsDropdownTID.UploadErrorToast(),
        onSettled: (newLegalTerms)=>{
            if (newLegalTerms) {
                onSelectLegalTerm(newLegalTerms);
            }
        },
        clearFileInput: ()=>{
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    });
    const handlePDFUpload = async (e)=>{
        e.preventDefault();
        const file = e.target.files && e.target.files[0];
        uploadLegalTerms(file);
    };
    const isLoading = [
        isLoadingLegalTerms,
        isUploadingLegalTerms,
        isDeletingLegalTerms,
        isRenamingLegalTerms
    ].some(Boolean);
    const selectedOption = useMemo(()=>{
        return selectedLegalTermId ? allLegalTerms.find((legalTerm)=>legalTerm.id === selectedLegalTermId) : undefined;
    }, [
        selectedLegalTermId,
        allLegalTerms
    ]);
    const isLastLegalTerm = useMemo(()=>{
        return allLegalTerms.length === 1;
    }, [
        allLegalTerms
    ]);
    const editVariant = editContext && sourceId ? {
        type: 'local',
        context: editContext,
        sourceId
    } : undefined;
    const showAnchorDefaultDecoration = UPDATE_DEFAULT_TOS_FE && !!selectedOption && legalTermsModel.isCurrentAnchorDefault(selectedOption);
    return /*#__PURE__*/ _jsxs(FullRow, {
        gap: 8,
        children: [
            /*#__PURE__*/ _jsx("input", {
                ref: fileInputRef,
                type: "file",
                accept: ".pdf",
                onChange: handlePDFUpload,
                "data-testid": LegalTermsDropdownTID.UploadFileInput(),
                hidden: true
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.LegalTermsDropdown,
                children: [
                    /*#__PURE__*/ _jsx(EnhancedAutocomplete, {
                        bi: {
                            name: 'legal_terms_template'
                        },
                        options: allLegalTerms,
                        isLoading: isLoading,
                        keyField: "id",
                        labelField: "name",
                        itemRenderer: (data)=>/*#__PURE__*/ _jsxs(FullRow, {
                                gap: 4,
                                justifyContent: "space-between",
                                alignItems: "start",
                                children: [
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        title: UPDATE_DEFAULT_TOS_FE && legalTermsModel.isCurrentAnchorDefault(data) && !(disabledItems === null || disabledItems === void 0 ? void 0 : disabledItems.some((item)=>item.id === data.id)) ? legalTermsTexts.v3InfoTooltip() : '',
                                        children: /*#__PURE__*/ _jsx("span", {
                                            className: styles.itemDetailsTooltipTrigger,
                                            children: /*#__PURE__*/ _jsxs(FullColumn, {
                                                gap: 4,
                                                alignItems: "start",
                                                children: [
                                                    /*#__PURE__*/ _jsxs(Row, {
                                                        gap: 8,
                                                        alignItems: "center",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Typography, {
                                                                skin: "secondary",
                                                                variant: "paragraph1",
                                                                testId: LegalTermsDropdownTID.Dropdown.Item.Name(data.id),
                                                                children: data.name
                                                            }),
                                                            UPDATE_DEFAULT_TOS_FE && legalTermsModel.isCurrentAnchorDefault(data) && /*#__PURE__*/ _jsx(Badge, {
                                                                skin: "primary",
                                                                rounded: true,
                                                                outlined: true,
                                                                testId: LegalTermsDropdownTID.Dropdown.Item.NewBadge(data.id),
                                                                children: legalTermsTexts.newBadge()
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        skin: "secondary",
                                                        variant: "paragraph2",
                                                        testId: LegalTermsDropdownTID.Dropdown.Item.PdfFileName(data.id),
                                                        children: data.pdfFileName || 'Text document'
                                                    })
                                                ]
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Row, {
                                        children: isAdvancedViewMode && /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                legalTermsModel.isDuplicatable(data) && /*#__PURE__*/ _jsx(IconButton, {
                                                    bi: {
                                                        name: 'duplicate_legal_terms'
                                                    },
                                                    tooltip: {
                                                        title: legalTermsTexts.duplicate(),
                                                        placement: 'top'
                                                    },
                                                    size: "medium",
                                                    onClick: (e)=>{
                                                        e.stopPropagation();
                                                        duplicateLegalTerms(data);
                                                    },
                                                    "data-testid": LegalTermsDropdownTID.Dropdown.Item.DuplicateButton(data.id),
                                                    children: /*#__PURE__*/ _jsx(Copy, {})
                                                }),
                                                /*#__PURE__*/ _jsx(IconButton, {
                                                    bi: {
                                                        name: 'edit_legal_terms'
                                                    },
                                                    tooltip: {
                                                        title: legalTermsModel.isEditable(data) ? 'Edit' : 'Rename',
                                                        placement: 'top'
                                                    },
                                                    size: "medium",
                                                    onClick: (e)=>{
                                                        e.stopPropagation();
                                                        if (legalTermsModel.isEditable(data)) {
                                                            editQuillLegalTerms(data, editVariant !== null && editVariant !== void 0 ? editVariant : {
                                                                type: 'global'
                                                            });
                                                        } else {
                                                            renameLegalTerms(data);
                                                        }
                                                    },
                                                    testId: LegalTermsDropdownTID.Dropdown.Item.EditButton(data.id),
                                                    children: /*#__PURE__*/ _jsx(Edit, {})
                                                }),
                                                !isLastLegalTerm && /*#__PURE__*/ _jsx(IconButton, {
                                                    bi: {
                                                        name: 'delete_legal_terms'
                                                    },
                                                    tooltip: {
                                                        title: legalTermsTexts.delete(),
                                                        placement: 'top'
                                                    },
                                                    size: "medium",
                                                    onClick: (e)=>{
                                                        e.stopPropagation();
                                                        deleteLegalTerms(data);
                                                    },
                                                    testId: LegalTermsDropdownTID.Dropdown.Item.DeleteButton(data.id),
                                                    children: /*#__PURE__*/ _jsx(Trash, {
                                                        size: 20
                                                    })
                                                })
                                            ]
                                        })
                                    })
                                ]
                            }),
                        size: "medium",
                        textFieldProps: {
                            bi: {
                                name: 'legal_terms_document'
                            },
                            placeholder: 'Choose a document',
                            label: legalTermsTexts.termsOfService(),
                            alwaysShrinkLabel: true,
                            inputRef
                        },
                        selectedOption: selectedOption,
                        applyOptionChange: (option)=>{
                            if (UPDATE_DEFAULT_TOS_FE && selectedOption && legalTermsModel.isLegacyAnchorDefault(selectedOption) && (option === null || option === void 0 ? void 0 : option.id) !== selectedOption.id) {
                                setReplacedLegacyIds((prev)=>{
                                    const next = new Set(prev);
                                    next.add(selectedOption.id);
                                    return next;
                                });
                            }
                            onSelectLegalTerm(option);
                        },
                        newItemOption: [
                            {
                                text: legalTermsTexts.createTextDocument(),
                                onClick: createQuillLegalTerms,
                                disabled: isMemberViewMode,
                                disabledTooltip: isMemberViewMode ? forbiddenTooltipText : undefined
                            },
                            {
                                text: legalTermsTexts.uploadPdf(),
                                icon: /*#__PURE__*/ _jsx(Import, {}),
                                onClick: async ()=>{
                                    var _fileInputRef_current;
                                    (_fileInputRef_current = fileInputRef.current) === null || _fileInputRef_current === void 0 ? void 0 : _fileInputRef_current.click();
                                },
                                disabled: isMemberViewMode,
                                disabledTooltip: isMemberViewMode ? forbiddenTooltipText : undefined
                            }
                        ],
                        promotionalActionOption: isBasicViewMode ? {
                            title: legalTermsTexts.manageTosPromotion.title(),
                            body: legalTermsTexts.manageTosPromotion.body(),
                            onClick: ()=>{
                                history.push(routes.proposalDefaults);
                            }
                        } : undefined,
                        emptySearchText: legalTermsTexts.emptySearchText(),
                        testId: LegalTermsDropdownTID.Dropdown.Root(),
                        disabled: disabled,
                        error: !!error,
                        helperText: error,
                        disabledItems: disabledItems
                    }),
                    showAnchorDefaultDecoration && selectedOption && /*#__PURE__*/ _jsxs("div", {
                        className: styles.anchorDefaultOverlay,
                        "aria-hidden": "true",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: styles.anchorDefaultMeasureText,
                                children: selectedOption.name
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: legalTermsTexts.v3InfoTooltip(),
                                placement: "top",
                                children: /*#__PURE__*/ _jsx("span", {
                                    "data-testid": LegalTermsDropdownTID.AnchorDefaultInfoIcon(),
                                    className: styles.anchorDefaultInfoIcon,
                                    children: /*#__PURE__*/ _jsx(Info, {
                                        size: 16
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: styles.anchorDefaultBadge,
                                children: /*#__PURE__*/ _jsx(Badge, {
                                    skin: "primary",
                                    rounded: true,
                                    outlined: true,
                                    testId: LegalTermsDropdownTID.AnchorDefaultNewBadge(),
                                    children: legalTermsTexts.newBadge()
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Row, {
                gap: 0,
                children: [
                    editVariant && selectedOption && legalTermsModel.isEditable(selectedOption) && /*#__PURE__*/ _jsx(IconButton, {
                        tooltip: {
                            title: "Edit for this ".concat(editVariant.context),
                            placement: 'top'
                        },
                        size: "medium",
                        testId: LegalTermsDropdownTID.EditDocumentButton(),
                        bi: {
                            name: 'edit_legal_terms'
                        },
                        onClick: ()=>{
                            editQuillLegalTerms(selectedOption, editVariant);
                        },
                        children: /*#__PURE__*/ _jsx(Edit, {})
                    }),
                    /*#__PURE__*/ _jsx(IconButton, {
                        tooltip: {
                            title: 'View',
                            placement: 'top'
                        },
                        type: "button",
                        size: "medium",
                        testId: LegalTermsDropdownTID.ViewLegalTermsButton(),
                        bi: {
                            name: 'view_legal_terms'
                        },
                        disabled: !selectedOption,
                        onClick: ()=>{
                            if (selectedOption) {
                                viewLegalTerms(selectedOption, sourceId);
                            }
                        },
                        children: /*#__PURE__*/ _jsx(Eye, {})
                    }),
                    onRemoveDropdown && /*#__PURE__*/ _jsx(IconButton, {
                        size: "medium",
                        disabled: disabled,
                        testId: LegalTermsDropdownTID.RemoveButton(),
                        bi: {
                            name: 'remove_legal_term'
                        },
                        onClick: ()=>onRemoveDropdown(selectedOption),
                        children: /*#__PURE__*/ _jsx(CloseCross, {})
                    })
                ]
            })
        ]
    });
};
